import type { OnboardingState } from '../components/Auth/AccountSetupBanner.types';

/**
 * Mock Onboarding Data for Demo and Testing
 *
 * These payloads represent different states of user onboarding progress
 * and can be used to test the AccountSetupBanner component.
 */

/**
 * Scenario 1: New user - no steps completed
 * User just signed up and needs to complete all onboarding steps
 */
export const MOCK_ONBOARDING_NEW_USER: OnboardingState = {
  shouldShowOnboardingBanner: true,
  completedSteps: [],
  remainingSteps: [
    {
      id: 'email',
      title: 'Verify email',
      description: 'Confirm your email address',
      completed: false,
      isAccessible: true, // First step is always accessible
      order: 0,
    },
    {
      id: 'investment-account',
      title: 'Create investment account',
      description: 'Set up your profile',
      completed: false,
      isAccessible: false, // Cannot access until email is verified
      order: 1,
    },
    {
      id: 'bank',
      title: 'Link bank account',
      description: 'Connect your funding source',
      completed: false,
      isAccessible: false,
      order: 2,
    },
    {
      id: 'identity',
      title: 'Verify identity',
      description: 'Complete KYC verification',
      completed: false,
      isAccessible: false,
      order: 3,
    },
  ],
  nextStep: {
    id: 'email',
    title: 'Verify email',
    description: 'Confirm your email address',
    completed: false,
    isAccessible: true,
    order: 0,
  },
  totalSteps: 4,
  completedCount: 0,
  progressPercentage: 0,
};

/**
 * Scenario 2: Email verified - 25% complete
 * User has verified email and can now create investment account
 */
export const MOCK_ONBOARDING_EMAIL_VERIFIED: OnboardingState = {
  shouldShowOnboardingBanner: true,
  completedSteps: ['email'],
  remainingSteps: [
    {
      id: 'investment-account',
      title: 'Create investment account',
      description: 'Set up your profile',
      completed: false,
      isAccessible: true, // Now accessible since email is verified
      order: 1,
    },
    {
      id: 'bank',
      title: 'Link bank account',
      description: 'Connect your funding source',
      completed: false,
      isAccessible: false,
      order: 2,
    },
    {
      id: 'identity',
      title: 'Verify identity',
      description: 'Complete KYC verification',
      completed: false,
      isAccessible: false,
      order: 3,
    },
  ],
  nextStep: {
    id: 'investment-account',
    title: 'Create investment account',
    description: 'Set up your profile',
    completed: false,
    isAccessible: true,
    order: 1,
  },
  totalSteps: 4,
  completedCount: 1,
  progressPercentage: 25,
};

/**
 * Scenario 3: Account created - 50% complete
 * User has created investment account, can now link bank
 */
export const MOCK_ONBOARDING_ACCOUNT_CREATED: OnboardingState = {
  shouldShowOnboardingBanner: true,
  completedSteps: ['email', 'investment-account'],
  remainingSteps: [
    {
      id: 'bank',
      title: 'Link bank account',
      description: 'Connect your funding source',
      completed: false,
      isAccessible: true, // Now accessible
      order: 2,
    },
    {
      id: 'identity',
      title: 'Verify identity',
      description: 'Complete KYC verification',
      completed: false,
      isAccessible: false,
      order: 3,
    },
  ],
  nextStep: {
    id: 'bank',
    title: 'Link bank account',
    description: 'Connect your funding source',
    completed: false,
    isAccessible: true,
    order: 2,
  },
  totalSteps: 4,
  completedCount: 2,
  progressPercentage: 50,
};

/**
 * Scenario 4: Bank linked - 75% complete
 * User has linked bank account, only KYC verification remaining
 */
export const MOCK_ONBOARDING_BANK_LINKED: OnboardingState = {
  shouldShowOnboardingBanner: true,
  completedSteps: ['email', 'investment-account', 'bank'],
  remainingSteps: [
    {
      id: 'identity',
      title: 'Verify identity',
      description: 'Complete KYC verification',
      completed: false,
      isAccessible: true, // Final step accessible
      order: 3,
    },
  ],
  nextStep: {
    id: 'identity',
    title: 'Verify identity',
    description: 'Complete KYC verification',
    completed: false,
    isAccessible: true,
    order: 3,
  },
  totalSteps: 4,
  completedCount: 3,
  progressPercentage: 75,
};

/**
 * Scenario 5: All steps complete
 * User has finished onboarding - banner should be hidden
 */
export const MOCK_ONBOARDING_COMPLETE: OnboardingState = {
  shouldShowOnboardingBanner: false, // Banner will not render
  completedSteps: ['email', 'investment-account', 'bank', 'identity'],
  remainingSteps: [],
  nextStep: null,
  totalSteps: 4,
  completedCount: 4,
  progressPercentage: 100,
};

/**
 * Default mock data for demo portal
 * Shows a user at 50% completion for demo purposes
 */
export const DEMO_ONBOARDING_DATA = MOCK_ONBOARDING_ACCOUNT_CREATED;

/**
 * All scenarios for testing
 */
export const ALL_ONBOARDING_SCENARIOS = {
  newUser: MOCK_ONBOARDING_NEW_USER,
  emailVerified: MOCK_ONBOARDING_EMAIL_VERIFIED,
  accountCreated: MOCK_ONBOARDING_ACCOUNT_CREATED,
  bankLinked: MOCK_ONBOARDING_BANK_LINKED,
  complete: MOCK_ONBOARDING_COMPLETE,
};
